import {
  type IndexUiState,
  type UiState,
} from 'instantsearch.js/es/types/ui-state'

/**
 * Query object for URL query string.
 * Explicit keys are used to remove unused parameter from the URL search parameters.
 */
export type ProductSearchAlgoliaRouteState = {
  category: string | undefined
  discountInPercent: { max?: string; min?: string } | undefined
  pageNumber: number | undefined
  priceMax: string | undefined
  priceMin: string | undefined
  q: string | undefined
  query: string | undefined
  ratingMax: string | undefined
  ratingMin: string | undefined
  refinementList: Partial<Record<string, string[]>> | undefined
  sortBy: string | undefined
}

const setPriceFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { range } = state

  if (range?.price) {
    const [priceMin, priceMax] = range.price.split(':')
    route.priceMin = priceMin || undefined
    route.priceMax = priceMax || undefined
  }
}

const setRatingFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { range } = state
  if (range?.averageRating) {
    const [ratingMin, ratingMax] = range.averageRating.split(':')
    route.ratingMin = ratingMin || undefined
    route.ratingMax = ratingMax || undefined
  }
}
const setDiscountFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { range } = state
  if (range?.discountInPercent) {
    const [discountMin, discountMax] = range.discountInPercent.split(':')
    route.discountInPercent = {
      max: discountMax || undefined,
      min: discountMin || undefined,
    }
  }
}
const setRefinementListFromState = (
  route: ProductSearchAlgoliaRouteState,
  state: IndexUiState,
): void => {
  const { refinementList } = state

  if (refinementList) {
    /**
     * Get the `refinementList.primaryCategory` and map it to a category URL search parameter.
     * This is to keep the URL as it currently behaves in Fock.
     */
    const { primaryCategory, ...restRefinementList } = refinementList

    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- this is wrongly typed in `RefinementListWidgetDescription` since this key may be absent
    route.category = primaryCategory?.at(-1)

    route.refinementList = restRefinementList
  }
}

/**
 * Transforms an index `ui-state` representation into `routeState`.
 * It receives an object that contains the index UI state of all the widgets on the page.
 * See https://www.algolia.com/doc/api-reference/widgets/instantsearch/react/#widget-param-routing.
 */
export const productSearchAlgoliaIndexUiStateToRoute = (
  uiState: UiState,
  {
    productsAlgoliaIndex,
    suggestProductsAlgoliaIndex,
  }: {
    productsAlgoliaIndex: string
    suggestProductsAlgoliaIndex?: string
  },
): ProductSearchAlgoliaRouteState => {
  const productsAlgoliaIndexState = uiState[productsAlgoliaIndex]
  const { page: pageNumber, sortBy } = productsAlgoliaIndexState

  let suggestProductsAlgoliaIndexState: IndexUiState | null = null

  if (suggestProductsAlgoliaIndex) {
    suggestProductsAlgoliaIndexState =
      uiState[suggestProductsAlgoliaIndex] ?? null
  }

  const query =
    suggestProductsAlgoliaIndexState?.query ?? productsAlgoliaIndexState.query

  const routeState: ProductSearchAlgoliaRouteState = {
    category: undefined,
    discountInPercent: undefined,
    pageNumber,
    priceMax: undefined,
    priceMin: undefined,
    /* eslint-disable-next-line id-length -- query param */
    q: query,
    query: undefined,
    ratingMax: undefined,
    ratingMin: undefined,
    refinementList: undefined,
    sortBy,
  }

  setPriceFromState(routeState, productsAlgoliaIndexState)
  setRatingFromState(routeState, productsAlgoliaIndexState)
  setRefinementListFromState(routeState, productsAlgoliaIndexState)
  setDiscountFromState(routeState, productsAlgoliaIndexState)

  return routeState
}
